// src/contact-form.ts
// Handles the NxtRev contact/quote form: service-chip selection, validation,
// spam honeypot check, and submission to Supabase. No UI/markup changes —
// this only wires up behavior for the existing form in contact.html.

import { createClient } from '@supabase/supabase-js'

// Uses your existing Vite env vars. Make sure these exist in your .env
// (and in Netlify's Environment Variables for production):
//   VITE_SUPABASE_URL=https://your-project.supabase.co
//   VITE_SUPABASE_ANON_KEY=your-anon-public-key
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

const supabase = createClient(supabaseUrl, supabaseAnonKey)

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('nxtrevContactForm') as HTMLFormElement | null
  if (!form) return

  const submitBtn = document.getElementById('nxtrevSubmitBtn') as HTMLButtonElement | null
  const submitBtnText = document.getElementById('nxtrevSubmitBtnText')
  const statusEl = document.getElementById('formStatusMessage') as HTMLDivElement | null
  const servicePillsContainer = document.getElementById('servicePills')
  const chips = servicePillsContainer?.querySelectorAll<HTMLButtonElement>('.chip')

  // --- Service chip selection (toggle .active on click, multi-select) ---
  const selectedServices = new Set<string>()

  chips?.forEach((chip) => {
    // Make chips keyboard focusable if not already a button
    if (chip.tagName !== 'BUTTON') {
      chip.setAttribute('tabindex', '0')
    }
    
    // Toggle function
    const toggleChip = () => {
      const service = chip.dataset.service
      if (!service) return

      if (chip.classList.contains('active')) {
        chip.classList.remove('active')
        chip.setAttribute('aria-checked', 'false')
        selectedServices.delete(service)
      } else {
        chip.classList.add('active')
        chip.setAttribute('aria-checked', 'true')
        selectedServices.add(service)
      }
    }

    // Mouse click
    chip.addEventListener('click', toggleChip)

    // Keyboard support (Space and Enter)
    chip.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        toggleChip()
      }
    })
  })

  // --- Status message helper (uses the existing card's visual language) ---
  function showStatus(message: string, type: 'success' | 'error') {
    if (!statusEl) return
    statusEl.textContent = message
    statusEl.classList.remove('hidden')
    if (type === 'success') {
      statusEl.style.background = 'rgba(34, 197, 94, 0.08)'
      statusEl.style.border = '1px solid rgba(34, 197, 94, 0.25)'
      statusEl.style.color = '#15803d'
    } else {
      statusEl.style.background = 'rgba(239, 68, 68, 0.08)'
      statusEl.style.border = '1px solid rgba(239, 68, 68, 0.25)'
      statusEl.style.color = '#b91c1c'
    }
  }

  function clearStatus() {
    if (!statusEl) return
    statusEl.classList.add('hidden')
    statusEl.textContent = ''
  }

  function setLoading(isLoading: boolean) {
    if (!submitBtn || !submitBtnText) return
    submitBtn.disabled = isLoading
    submitBtn.style.opacity = isLoading ? '0.7' : '1'
    submitBtn.style.cursor = isLoading ? 'not-allowed' : 'pointer'
    submitBtnText.textContent = isLoading ? 'Sending…' : 'Send my message'
  }

  // --- Submit handler ---
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    clearStatus()

    const formData = new FormData(form)

    // Honeypot spam check — if this hidden field has a value, silently bail
    // without showing an error (bots shouldn't learn they were blocked).
    const honeypot = (formData.get('honeypot_website') as string) || ''
    if (honeypot.trim() !== '') {
      console.warn('Honeypot triggered — submission ignored.')
      return
    }

    const fullName = (formData.get('fullName') as string || '').trim()
    const email = (formData.get('email') as string || '').trim()
    const phone = (formData.get('phone') as string || '').trim()
    const businessType = (formData.get('businessType') as string || '').trim()
    const projectDetails = (formData.get('projectDetails') as string || '').trim()

    // Basic client-side validation (server-side/RLS still enforces real rules)
    if (!fullName || !email || !phone || !projectDetails) {
      showStatus('Please fill in all required fields.', 'error')
      return
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(email)) {
      showStatus('Please enter a valid email address.', 'error')
      return
    }

    setLoading(true)

    try {
      // Trigger the backend edge function to handle the secure insert & notifications
      const { data, error: functionError } = await supabase.functions.invoke('handle-new-lead', {
        body: {
          full_name: fullName,
          email,
          phone,
          business_type: businessType || null,
          project_details: projectDetails,
          services: Array.from(selectedServices),
          honeypot: honeypot, // Passed to backend for server-side validation
        }
      });
      
      if (functionError) {
        console.error('Edge function invocation failed:', functionError);
        
        // If it's our specific rate limit error, show that to the user instead of the generic error
        if (functionError.message && functionError.message.includes('recently')) {
          showStatus(functionError.message, 'error');
        } else if (functionError.context && functionError.context.error && typeof functionError.context.error === 'string') {
          showStatus(functionError.context.error, 'error');
        } else {
          showStatus(
            "Something went wrong sending your message. Please try WhatsApp instead, or email contact@nxtrev.in directly.",
            'error'
          )
        }
        setLoading(false)
        return
      }

      console.log('Edge function response:', data);

      showStatus(
        "Message sent — thank you! We'll get back to you within 24 hours with a clear technical proposal.",
        'success'
      )
      form.reset()
      chips?.forEach((chip) => {
        chip.classList.remove('active')
        chip.setAttribute('aria-checked', 'false')
      })
      selectedServices.clear()
    } catch (err) {
      console.error('Unexpected error submitting form:', err)
      showStatus(
        "Something went wrong sending your message. Please try WhatsApp instead, or email contact@nxtrev.in directly.",
        'error'
      )
    } finally {
      setLoading(false)
    }
  })
})
